import { useState, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { Calendar } from 'lucide-react';
import type { ComponentProps } from 'react';

export interface DateInputProps extends Omit<ComponentProps<'input'>, 'type' | 'value' | 'onChange'> {
  value: string; // ISO date string (YYYY-MM-DD)
  onChange: (value: string) => void;
}

export function DateInput({
  value,
  onChange,
  className,
  ...props
}: DateInputProps) {
  // Convert ISO date (YYYY-MM-DD) to Brazilian format (DD/MM/YYYY) for display
  const formatToBrazilian = (isoDate: string): string => {
    if (!isoDate) return '';
    const [year, month, day] = isoDate.split('-');
    return `${day}/${month}/${year}`;
  };

  // Convert Brazilian format (DD/MM/YYYY) to ISO date (YYYY-MM-DD)
  const formatToISO = (brazilianDate: string): string => {
    const cleaned = brazilianDate.replace(/\D/g, '');
    if (cleaned.length !== 8) return '';

    const day = cleaned.substring(0, 2);
    const month = cleaned.substring(2, 4);
    const year = cleaned.substring(4, 8);

    return `${year}-${month}-${day}`;
  };

  const [displayValue, setDisplayValue] = useState(formatToBrazilian(value));
  const nativeDateInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDisplayValue(formatToBrazilian(value));
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Remove all non-numeric characters
    const cleaned = inputValue.replace(/\D/g, '');

    // Format as DD/MM/YYYY
    let formatted = '';
    if (cleaned.length > 0) {
      formatted = cleaned.substring(0, 2);
      if (cleaned.length >= 3) {
        formatted += '/' + cleaned.substring(2, 4);
      }
      if (cleaned.length >= 5) {
        formatted += '/' + cleaned.substring(4, 8);
      }
    }

    setDisplayValue(formatted);

    // If we have a complete date (8 digits), convert to ISO and call onChange
    if (cleaned.length === 8) {
      const isoDate = formatToISO(formatted);

      // Validate the date
      const day = parseInt(cleaned.substring(0, 2));
      const month = parseInt(cleaned.substring(2, 4));
      const year = parseInt(cleaned.substring(4, 8));

      if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
        onChange(isoDate);
      }
    }
  };

  const handleBlur = () => {
    // On blur, if we have a partial date, try to complete it
    const cleaned = displayValue.replace(/\D/g, '');

    if (cleaned.length === 8) {
      const isoDate = formatToISO(displayValue);
      onChange(isoDate);
    } else if (cleaned.length > 0) {
      // Reset to the last valid value
      setDisplayValue(formatToBrazilian(value));
    }
  };

  const handleNativeDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (newValue) {
      onChange(newValue);
      setDisplayValue(formatToBrazilian(newValue));
    }
  };

  const handleCalendarClick = () => {
    nativeDateInputRef.current?.showPicker();
  };

  return (
    <div className="relative flex items-center">
      <input
        type="text"
        data-slot="date-input"
        className={twMerge(
          'flex h-10 w-full rounded-lg border border-input bg-surface px-3 py-2 pr-10 text-sm',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'placeholder:text-foreground-subtle',
          className
        )}
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="DD/MM/AAAA"
        maxLength={10}
        {...props}
      />

      {/* Hidden native date input */}
      <input
        ref={nativeDateInputRef}
        type="date"
        className="absolute opacity-0 pointer-events-none"
        value={value}
        onChange={handleNativeDateChange}
        tabIndex={-1}
      />

      {/* Calendar button */}
      <button
        type="button"
        onClick={handleCalendarClick}
        className="absolute right-2 flex items-center justify-center h-6 w-6 text-foreground-subtle hover:text-foreground transition-colors"
        tabIndex={-1}
      >
        <Calendar className="size-4" />
      </button>
    </div>
  );
}
